import { useMemo } from 'react';
import { photoUrlCache } from '../utils/photoUrlCache';

/**
 * Custom hook to cache pet photo URLs
 * 
 * This hook wraps the photoUrlCache to provide a React-friendly interface.
 * It caches photo URLs to prevent unnecessary image reloads when the same
 * pet data is fetched multiple times with different presigned URLs.
 * 
 * @param petId - The unique ID of the pet
 * @param photoUrl - The photo URL from the API (may be a new presigned URL each time)
 * @returns The cached photo URL (or the new one if not cached)
 * 
 * @example
 * const cachedPhotoUrl = useCachedPhotoUrl(pet.id, pet.photo_url);
 * <img src={cachedPhotoUrl || '/default-pet.png'} alt={pet.name} />
 */
export function useCachedPhotoUrl(
  petId: string | undefined, 
  photoUrl: string | null | undefined
): string | null {
  return useMemo(() => {
    if (!petId || !photoUrl) {
      return null;
    }

    // Try to use cached URL, otherwise cache and return the new URL
    return photoUrlCache.getOrSet(petId, photoUrl);
  }, [petId, photoUrl]);
}

/**
 * Hook to clear a pet's cached photo URL
 * Use this when the pet's photo is updated/changed
 * 
 * @param petId - The unique ID of the pet
 * @returns A function to clear the cache for this pet
 * 
 * @example
 * const clearPhotoCache = useClearPhotoCache(pet.id);
 * // After uploading a new photo:
 * clearPhotoCache();
 */
export function useClearPhotoCache(petId: string | undefined) {
  return useMemo(() => {
    return () => {
      if (petId) {
        photoUrlCache.clear(petId);
      }
    };
  }, [petId]);
}

